/**
 * Single source of truth for every Strelve-owned domain, host, and email address.
 *
 * Nothing outside this file may hardcode a `strelve.*` literal. The guard test
 * `tests/config/domain-literals.test.ts` fails the build if it does — that check
 * exists because the shortstack.co.nz -> strelve.com rename leaked stale domains
 * into lead-facing emails and pages that only surfaced during manual testing.
 *
 * Market host assignments are recorded separately from routing state. This
 * lets us prepare future market URLs without changing which domains serve
 * production traffic or redirect today.
 */

import {
  DEFAULT_MARKET_ID,
  getMarketIdForCountryCode,
  MARKET_IDS,
  MARKET_PROFILES,
  type MarketId,
  type MarketProfile,
} from "./markets";

export {
  DEFAULT_MARKET_ID,
  MARKET_IDS,
  getMarketIdForCountryCode,
} from "./markets";
export type { MarketId } from "./markets";

type MarketDomainDefinition = {
  domain: string;
  canonicalHost: string;
};

/**
 * Domain assignments are kept apart from country, locale, and currency
 * profiles. Adding an entry here does not activate a domain;
 * MARKET_DOMAIN_STATUS controls production host behavior below.
 */
const MARKET_DOMAINS = {
  nz: {
    domain: "strelve.co.nz",
    canonicalHost: "www.strelve.co.nz",
  },
  au: {
    domain: "strelve.com.au",
    canonicalHost: "www.strelve.com.au",
  },
  us: {
    domain: "strelve.com",
    canonicalHost: "www.strelve.com",
  },
} as const satisfies Record<MarketId, MarketDomainDefinition>;

/** Combined market details retained for existing domain and SEO consumers. */
export const MARKETS = {
  nz: { ...MARKET_PROFILES.nz, ...MARKET_DOMAINS.nz },
  au: { ...MARKET_PROFILES.au, ...MARKET_DOMAINS.au },
  us: { ...MARKET_PROFILES.us, ...MARKET_DOMAINS.us },
} as const satisfies Record<MarketId, MarketProfile & MarketDomainDefinition>;

type MarketDomainStatus = "active" | "redirecting" | "planned";

/**
 * Current domain routing state, kept distinct from self-serve market availability.
 * The AU host is active; the US host continues redirecting to NZ.
 */
const MARKET_DOMAIN_STATUS: Record<MarketId, MarketDomainStatus> = {
  nz: "active",
  au: "active",
  us: "redirecting",
};

export function isMarketingOnlyMarket(marketId: MarketId): boolean {
  return (process.env.NEXT_PUBLIC_AU_SELF_SERVE_ENABLED === "true" ? [] : ["au"] as readonly MarketId[]).includes(marketId);
}

type MarketDomainRoute = {
  hostname: string;
  marketId: MarketId;
  status: MarketDomainStatus;
  canonical: boolean;
};

const MARKET_DOMAIN_ROUTES: readonly MarketDomainRoute[] = MARKET_IDS.flatMap(
  (marketId) => {
    const market = MARKETS[marketId];
    const status = MARKET_DOMAIN_STATUS[marketId];

    return [
      {
        hostname: market.domain,
        marketId,
        status,
        canonical: false,
      },
      {
        hostname: market.canonicalHost,
        marketId,
        status,
        canonical: true,
      },
    ];
  }
);

function normaliseMarketHostname(hostname: string): string {
  return hostname
    .split(":")[0]
    .trim()
    .toLowerCase()
    .replace(/\.$/, "");
}

/** Returns a declared host assignment, including planned and redirecting hosts. */
export function getMarketDomainRoute(
  hostname: string | null | undefined
): MarketDomainRoute | null {
  if (!hostname) return null;
  const normalized = normaliseMarketHostname(hostname);
  const configuredRoute = MARKET_DOMAIN_ROUTES.find((route) => route.hostname === normalized);
  if (configuredRoute) return configuredRoute;

  if (isLocalDevelopmentEnvironment()) {
    const auLocalHosts = [
      `${MARKETS.au.domain}.localhost`,
      `www.${MARKETS.au.domain}.localhost`,
    ];
    if (auLocalHosts.includes(normalized)) {
      return {
        hostname: normalized,
        marketId: "au",
        status: "active",
        canonical: normalized.startsWith("www."),
      };
    }
  }

  return null;
}

function isLocalDevelopmentEnvironment(): boolean {
  return process.env.NODE_ENV === "development" && process.env.VERCEL !== "1";
}

export function getLocalDevelopmentMarketHostname(marketId: MarketId): string {
  return marketId === "au"
    ? `www.${MARKETS.au.domain}.localhost`
    : "localhost";
}

export function isLocalDevelopmentMarketHost(
  hostname: string | null | undefined,
  marketId: MarketId,
): boolean {
  if (!hostname || !isLocalDevelopmentEnvironment()) return false;
  const normalized = normaliseMarketHostname(hostname);
  return marketId === "au" && [
    getLocalDevelopmentMarketHostname(marketId),
    `${MARKETS.au.domain}.localhost`,
  ].includes(normalized);
}

/** Returns a market only when its domain is currently serving the app directly. */
export function getActiveMarketIdForHost(
  hostname: string | null | undefined
): MarketId | null {
  const route = getMarketDomainRoute(hostname);
  return route?.status === "active" ? route.marketId : null;
}

/** Markets whose first-party domains currently serve the app directly. */
export function getActiveMarketIds(): MarketId[] {
  return MARKET_IDS.filter((marketId) => MARKET_DOMAIN_STATUS[marketId] === "active");
}

export function getMarketOrigin(marketId: MarketId): string {
  return `https://${MARKETS[marketId].canonicalHost}`;
}

/** Builds an absolute URL for a path on the market's canonical host. */
export function getMarketPublicUrl(marketId: MarketId, path = "/"): string {
  const url = new URL(getMarketOrigin(marketId));
  url.pathname = path.startsWith("/") ? path : `/${path}`;
  return url.toString();
}

/** Canonical registrable domain. NZ remains the active default market. */
export const PRIMARY_DOMAIN = MARKETS[DEFAULT_MARKET_ID].domain;

/** Canonical host serving the app. Everything else 301s here. */
export const PRIMARY_HOST = MARKETS[DEFAULT_MARKET_ID].canonicalHost;

/** Canonical production origin. Default for every outbound/emailed link. */
export const PRODUCTION_APP_URL = getMarketOrigin(DEFAULT_MARKET_ID);

/** Stable HTTPS origin for app links generated by Vercel Preview deployments. */
export const STAGING_APP_URL = `https://staging.${PRIMARY_DOMAIN}`;

/**
 * Every registrable domain Strelve owns, current and legacy.
 *
 * Legacy entries stay forever: they must never be claimable by a client as a
 * custom domain or sender domain, and old links must keep resolving.
 *
 * shortstack.co.nz (pre-rename name) is deliberately NOT listed here even
 * though Strelve still owns it — 2026-08-16, unblocked on request so it can
 * be used as a real custom-domain test fixture (301s to strelve.co.nz,
 * no meaningful traffic since the rename).
 */
const CONFIGURED_MARKET_IDS = MARKET_IDS.filter(
  (marketId) => MARKET_DOMAIN_STATUS[marketId] !== "planned"
);

export const OWNED_DOMAINS: readonly string[] = Object.freeze(
  CONFIGURED_MARKET_IDS.map((marketId) => MARKETS[marketId].domain)
);

/**
 * Hosts that count as production. Used to suppress staging diagnostics and to
 * enable Sentry. Must list every owned domain with and without `www.` — a host
 * missing here silently leaks developer diagnostics to real customers.
 */
const PRODUCTION_HOSTS: readonly string[] = MARKET_DOMAIN_ROUTES
  .filter((route) => route.status !== "planned")
  .map((route) => route.hostname);

export function isProductionHost(hostname: string | null | undefined): boolean {
  if (!hostname) return false;
  return PRODUCTION_HOSTS.includes(hostname.split(":")[0].toLowerCase());
}

/**
 * Production hosts that are no longer canonical and 301 to PRIMARY_HOST.
 *
 * These stay in PRODUCTION_HOSTS so staging diagnostics remain suppressed while
 * they still serve traffic. Retiring a market means moving its hosts here, not
 * deleting them — old links and inboxes keep pointing at them indefinitely.
 */
const LEGACY_PRODUCTION_HOSTS: readonly string[] = MARKET_DOMAIN_ROUTES
  .filter((route) => route.status === "redirecting")
  .map((route) => route.hostname);

export function isLegacyProductionHost(hostname: string | null | undefined): boolean {
  if (!hostname) return false;
  return LEGACY_PRODUCTION_HOSTS.includes(hostname.split(":")[0].toLowerCase());
}

/** Hosts the app serves directly (as opposed to a client custom domain). */
export function isStrelveHost(hostname: string | null | undefined): boolean {
  if (!hostname) return false;
  const host = hostname.split(":")[0].toLowerCase();
  return (
    isProductionHost(host) ||
    Boolean(getMarketDomainRoute(host)) ||
    OWNED_DOMAINS.some((domain) => host === domain || host.endsWith(`.${domain}`)) ||
    host.endsWith(".vercel.app") ||
    host === "localhost" ||
    host === "127.0.0.1"
  );
}

// ─── Email addresses ───
// RESEND_FROM_EMAIL is the rollback lever: if the .co.nz sending domain is not
// yet verified in Resend, set it to the previously verified address rather than
// editing this file.

export const SUPPORT_EMAIL = `support@${PRIMARY_DOMAIN}`;
export const HELLO_EMAIL = `hello@${PRIMARY_DOMAIN}`;
export const NOTIFICATIONS_EMAIL = `notifications@${PRIMARY_DOMAIN}`;

/** Use the Australian support alias on the Australian public site. */
export function getSupportEmailForMarket(marketId: MarketId): string {
  return marketId === "au" ? `support@${MARKETS.au.domain}` : SUPPORT_EMAIL;
}

export function getHelloEmailForMarket(marketId: MarketId): string {
  return marketId === "au" ? `hello@${MARKETS.au.domain}` : HELLO_EMAIL;
}

/** Sending subdomain for automated lead-facing follow-up and re-engagement email. */
const FOLLOW_UP_SENDING_DOMAINS: Record<MarketId, string> = {
  nz: `followup.${MARKETS.nz.domain}`,
  au: `followup.${MARKETS.au.domain}`,
  us: `followup.${MARKETS.us.domain}`,
};

export const FOLLOW_UP_SENDING_DOMAIN = FOLLOW_UP_SENDING_DOMAINS[DEFAULT_MARKET_ID];
export const FOLLOW_UP_FROM_EMAIL = `follow-up@${FOLLOW_UP_SENDING_DOMAIN}`;

export function getFollowUpSendingDomainForMarket(marketId: MarketId): string {
  return FOLLOW_UP_SENDING_DOMAINS[marketId];
}

/** Use a market-specific sender for public contact messages. */
export function getNotificationsFromEmailForMarket(marketId: MarketId): string {
  return marketId === "au"
    ? `notifications@${MARKETS.au.domain}`
    : (process.env.RESEND_FROM_EMAIL?.trim() || NOTIFICATIONS_EMAIL);
}

export function isFollowUpSenderEmail(
  value: string | undefined,
  marketId: MarketId = DEFAULT_MARKET_ID,
): boolean {
  const match = value?.trim().match(/^[^@\s<>]+@([^@\s<>]+)$/);
  return match?.[1]?.toLowerCase() === getFollowUpSendingDomainForMarket(marketId);
}

/** Returns the configured follow-up sender when its market key is available. */
export function getFollowUpFromEmailForMarket(marketId: MarketId): string | undefined {
  const isAustralia = marketId === "au";
  const configured = (isAustralia
    ? process.env.RESEND_FOLLOW_UP_FROM_EMAIL_AU
    : process.env.RESEND_FOLLOW_UP_FROM_EMAIL)?.trim();
  const followUpApiKey = isAustralia
    ? process.env.RESEND_API_KEY_AU_FOLLOW_UP
    : process.env.RESEND_API_KEY_NZ_FOLLOW_UP;

  if (!followUpApiKey?.trim()) return undefined;
  return isFollowUpSenderEmail(configured, marketId) ? configured : undefined;
}

/**
 * Subdomain that receives lead replies. Parsing accepts every historic value so
 * threads already in a lead's inbox keep routing after a domain move.
 */
export const REPLY_ALIAS_DOMAIN = `reply.${PRIMARY_DOMAIN}`;
const MARKET_REPLY_ALIAS_DOMAINS: Partial<Record<MarketId, string>> = {
  nz: REPLY_ALIAS_DOMAIN,
  au: `reply.${MARKETS.au.domain}`,
};

export function getReplyAliasDomainForCountryCode(
  countryCode: string | null | undefined
): string {
  const marketId = getMarketIdForCountryCode(countryCode);
  return (marketId && MARKET_REPLY_ALIAS_DOMAINS[marketId]) || REPLY_ALIAS_DOMAIN;
}

export const ACCEPTED_REPLY_ALIAS_DOMAINS: readonly string[] = [
  ...Object.values(MARKET_REPLY_ALIAS_DOMAINS),
  "reply.strelve.com",
];

/**
 * Domain suffixes a client may never claim as a custom domain or sender domain.
 * Covers Strelve-owned domains plus infrastructure hosts.
 */
export const RESERVED_DOMAIN_SUFFIXES: readonly string[] = [
  ...OWNED_DOMAINS,
  "vercel.app",
];

export const RESERVED_SENDER_DOMAIN_SUFFIXES: readonly string[] = [
  ...OWNED_DOMAINS,
  "resend.com",
  "amazonses.com",
];
